import { useAuth } from 'context/useAuth';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  QueryConstraint,
  QueryDocumentSnapshot,
  startAfter,
  where,
} from 'firebase/firestore';
import { ClientInterface } from 'functions/hooks/useGetUsers';

export type ClientSortLabel = 'Latest' | 'A - Z' | 'Z - A' | 'Active Alerts';

interface UseClientsSearchOptions {
  query?: string;
  sort?: ClientSortLabel;
  pageSize?: number;
}

interface UseClientsSearchResult {
  users: ClientInterface[];
  loading: boolean;
  error: string;
  pageIndex: number;
  hasNext: boolean;
  hasPrev: boolean;
  next: () => void;
  prev: () => void;
  reset: () => void;
}

/**
 * Lowercases + trims a single token, keeps digits/letters/spaces intact.
 * Mirrors the server-side normaliser used by buildUserSearchPayload so the
 * query token always matches one of the precomputed keyword prefixes.
 *
 * Phone numbers: a leading '+' is stripped so '+27...' matches the international
 * digit-only keyword stored on the user (e.g. '27731239723'). The server emits
 * three phone variants per user (intl '27...', national '0...', national-no-zero
 * '7...') and indexes every prefix of each, so any of those prefixes matches.
 */
function normaliseToken(input: string): string {
  return (input || '').toLocaleLowerCase().trim().replace(/^\+/, '');
}

function mapDocToClient(docId: string, data: any): ClientInterface {
  const s = data?.search || {};
  const profile = data?.profile || {};
  return {
    id: docId,
    firstName: profile.firstName || '',
    lastName: profile.lastName || '',
    email: profile.email || '',
    idNumber: profile.idNumber || '',
    phoneCode: profile.phoneCode || '',
    phoneNumber: profile.phoneNumber || '',
    idNumberIsSouthAfrican: !!profile.idNumberIsSouthAfrican,
    auraDisabled: !!data?.integration?.auraDisabled,
    threeDtPackage: data?.integration?.threeDtPackage || '',
    notification: data?.notification || ({} as any),
    createdOnDateTime:
      typeof s.createdAt?.toDate === 'function'
        ? s.createdAt.toDate().toISOString()
        : data?.createdAt?.toDate
        ? data.createdAt.toDate().toISOString()
        : '',
    tamperActive: !!s.tamperActive,
    totalNumberOfVehicles: Array.isArray(s.vehicleRegistrationsLower)
      ? s.vehicleRegistrationsLower.length
      : 0,
    vehicleRegistrationNumbersStr: Array.isArray(s.vehicleRegistrationsLower)
      ? s.vehicleRegistrationsLower.join('|')
      : '',
    deviceIdsStr: Array.isArray(s.deviceIds) ? s.deviceIds.join('|') : '',
    remoteIdsStr: Array.isArray(s.remoteSerialNumbers)
      ? s.remoteSerialNumbers.join('|')
      : '',
  };
}

/**
 * Builds the orderBy + __name__ tiebreaker pair for a given sort label.
 * Critical: the __name__ direction must lockstep with the primary sort
 * direction so the query exactly matches one of the provisioned composite
 * indexes (see pagination-proposal.md §9).
 */
function buildOrderBy(sort: ClientSortLabel): QueryConstraint[] {
  switch (sort) {
    case 'A - Z':
      return [orderBy('search.fullNameLower', 'asc'), orderBy('__name__', 'asc')];
    case 'Z - A':
      return [orderBy('search.fullNameLower', 'desc'), orderBy('__name__', 'desc')];
    case 'Active Alerts':
      return [
        orderBy('search.activeAlertSortKey', 'desc'),
        orderBy('search.createdAt', 'desc'),
        orderBy('__name__', 'desc'),
      ];
    case 'Latest':
    default:
      return [orderBy('search.createdAt', 'desc'), orderBy('__name__', 'desc')];
  }
}

export default function useClientsSearch(
  options: UseClientsSearchOptions = {}
): UseClientsSearchResult {
  const { query: rawQuery = '', sort = 'Latest', pageSize = 10 } = options;
  const { firestore } = useAuth();

  const [users, setUsers] = useState<ClientInterface[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [hasNext, setHasNext] = useState(false);

  // Cursor stack: cursorStack[i] is the document AFTER which page i starts.
  // Page 0 has no startAfter (use the first page directly).
  const cursorsRef = useRef<Array<QueryDocumentSnapshot | null>>([null]);
  // Track the last doc of the current page so we can push a new cursor on `next()`.
  const lastDocRef = useRef<QueryDocumentSnapshot | null>(null);

  // Tracks whether the component is still mounted, and which fetch is the
  // latest in flight. Older responses are dropped to prevent races.
  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const normalisedQuery = useMemo(() => normaliseToken(rawQuery), [rawQuery]);

  const fetchPage = useCallback(
    async (targetPageIndex: number) => {
      if (!firestore) return;
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError('');

      try {
        const constraints: QueryConstraint[] = [
          where('search.isAccountHolder', '==', true),
        ];

        if (normalisedQuery) {
          constraints.push(
            where('search.keywords', 'array-contains', normalisedQuery)
          );
        }

        constraints.push(...buildOrderBy(sort));

        const cursor = cursorsRef.current[targetPageIndex] ?? null;
        if (cursor) {
          constraints.push(startAfter(cursor));
        }

        // Fetch one extra doc to determine if there is a next page.
        constraints.push(limit(pageSize + 1));

        const q = query(collection(firestore, 'users'), ...constraints);
        const snap = await getDocs(q);

        if (!mountedRef.current || requestId !== requestIdRef.current) return;

        const docs = snap.docs;
        const pageDocs = docs.slice(0, pageSize);
        const hasNextPage = docs.length > pageSize;

        setUsers(pageDocs.map((d) => mapDocToClient(d.id, d.data())));
        setHasNext(hasNextPage);
        setPageIndex(targetPageIndex);
        lastDocRef.current = pageDocs[pageDocs.length - 1] ?? null;
      } catch (err: any) {
        if (!mountedRef.current || requestId !== requestIdRef.current) return;
        // Surface Firestore precondition errors verbatim — they almost always
        // indicate a missing composite index (see §9 of pagination-proposal.md).
        setError(err?.message || String(err));
        setUsers([]);
        setHasNext(false);
      } finally {
        if (mountedRef.current && requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [firestore, normalisedQuery, sort, pageSize]
  );

  // Reset cursors and refetch from page 0 whenever query/sort/pageSize changes.
  useEffect(() => {
    cursorsRef.current = [null];
    lastDocRef.current = null;
    fetchPage(0);
  }, [fetchPage]);

  const next = useCallback(() => {
    if (!hasNext || loading) return;
    const nextIndex = pageIndex + 1;
    // Record the last doc of the current page as the cursor for the next page.
    if (cursorsRef.current.length <= nextIndex) {
      cursorsRef.current[nextIndex] = lastDocRef.current;
    }
    fetchPage(nextIndex);
  }, [hasNext, loading, pageIndex, fetchPage]);

  const prev = useCallback(() => {
    if (pageIndex <= 0 || loading) return;
    fetchPage(pageIndex - 1);
  }, [pageIndex, loading, fetchPage]);

  const reset = useCallback(() => {
    cursorsRef.current = [null];
    lastDocRef.current = null;
    fetchPage(0);
  }, [fetchPage]);

  return {
    users,
    loading,
    error,
    pageIndex,
    hasNext,
    hasPrev: pageIndex > 0,
    next,
    prev,
    reset,
  };
}
